import { useCallback, useEffect, useRef, useState } from "react";
import type { OfficeActionId } from "@/lib/office/actions";
import type { PeerSnapshot, PersonalOfficeSnapshot, ServerMessage } from "@/lib/office/protocol";
import { SPATIAL_AV_TIP } from "./constants";
import { DEFAULT_ICE_SERVERS, SpatialMediaSession } from "./spatial-media";
import type { AvatarColors, VyllageWorldElement } from "./world-engine";

/** Load published map geometry (by mapId) and apply to the world engine. */
async function fetchAndApplyMapLayout(
  world: VyllageWorldElement | null | undefined,
  mapId: string = "campus",
): Promise<void> {
  if (!world?.applyMapLayout) return;
  try {
    const qs = mapId && mapId !== "campus" ? `?mapId=${encodeURIComponent(mapId)}` : "";
    const res = await fetch(`/api/map-layout${qs}`, { credentials: "include" });
    if (!res.ok) return;
    const body: unknown = await res.json();
    if (
      body &&
      typeof body === "object" &&
      "data" in body &&
      body.data &&
      typeof body.data === "object" &&
      "layout" in (body.data as object)
    ) {
      const layout = (body.data as { layout: Parameters<VyllageWorldElement["applyMapLayout"]>[0] })
        .layout;
      world.applyMapLayout(layout);
    }
  } catch {
    // Keep code-default geometry if fetch fails.
  }
}

/** Fetch ICE config from the authenticated endpoint; fall back to default STUN. */
async function fetchOfficeIceServers(): Promise<RTCIceServer[]> {
  try {
    const res = await fetch("/api/office/ice", { credentials: "include" });
    if (!res.ok) return DEFAULT_ICE_SERVERS;
    const body: unknown = await res.json();
    if (
      body &&
      typeof body === "object" &&
      "iceServers" in body &&
      Array.isArray((body as { iceServers: unknown }).iceServers) &&
      (body as { iceServers: RTCIceServer[] }).iceServers.length > 0
    ) {
      return (body as { iceServers: RTCIceServer[] }).iceServers;
    }
  } catch {
    // Network / parse failure — STUN fallback is fine for same-LAN HQ.
  }
  return DEFAULT_ICE_SERVERS;
}

export type ConnectionStatus = "idle" | "connecting" | "connected" | "error";

type UseOfficeMultiplayerArgs = {
  enabled: boolean;
  name: string;
  colors: AvatarColors;
  status: string;
  mic: boolean;
  cam: boolean;
  worldRef: React.RefObject<VyllageWorldElement | null>;
  onChat: (msg: {
    id: string;
    from: string;
    name: string;
    color: string;
    text: string;
    zone: string;
    vol: number;
  }) => void;
  onPeerAction: (name: string, action: OfficeActionId) => void;
  onToast: (text: string) => void;
  /** Align React mic/cam flags when local tracks are missing after gUM (Key Decision 12). */
  onMediaFlagsAlign?: (flags: { mic: boolean; cam: boolean }) => void;
  onOffices?: (offices: PersonalOfficeSnapshot[]) => void;
};

export function useOfficeMultiplayer({
  enabled,
  name,
  colors,
  status,
  mic,
  cam,
  worldRef,
  onChat,
  onPeerAction,
  onToast,
  onMediaFlagsAlign,
  onOffices,
}: UseOfficeMultiplayerArgs) {
  const [connection, setConnection] = useState<ConnectionStatus>("idle");
  const [peerCount, setPeerCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** Local gUM stream for meeting-mode video tiles (also attached to world cam). */
  const [localStream, setLocalStreamState] = useState<MediaStream | null>(null);
  /** Peer id → remote MediaStream for Zoom-style grid. */
  const [remoteStreams, setRemoteStreams] = useState<Record<string, MediaStream>>({});
  /** Current map the local player is on (hq | plaza | …). */
  const [mapId, setMapId] = useState("campus");
  /** Local client is screen-sharing (meeting view). */
  const [sharing, setSharing] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRef = useRef<SpatialMediaSession | null>(null);
  const selfIdRef = useRef<string>("");
  const peersRef = useRef<Map<string, PeerSnapshot>>(new Map());
  const mapIdRef = useRef("campus");
  const transitioningRef = useRef(false);
  const onChatRef = useRef(onChat);
  const onPeerActionRef = useRef(onPeerAction);
  const onToastRef = useRef(onToast);
  const onMediaFlagsAlignRef = useRef(onMediaFlagsAlign);
  const onOfficesRef = useRef(onOffices);
  const nameRef = useRef(name);
  const colorsRef = useRef(colors);
  const statusRef = useRef(status);
  const micRef = useRef(mic);
  const camRef = useRef(cam);
  const sharingRef = useRef(false);
  onChatRef.current = onChat;
  onPeerActionRef.current = onPeerAction;
  onToastRef.current = onToast;
  onMediaFlagsAlignRef.current = onMediaFlagsAlign;
  onOfficesRef.current = onOffices;
  nameRef.current = name;
  colorsRef.current = colors;
  statusRef.current = status;
  micRef.current = mic;
  camRef.current = cam;
  mapIdRef.current = mapId;

  const applyOffices = useCallback(
    (offices: PersonalOfficeSnapshot[]) => {
      worldRef.current?.setPersonalOffices?.(offices);
      onOfficesRef.current?.(offices);
    },
    [worldRef],
  );

  const send = useCallback((msg: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  const flushPeersToWorld = useCallback(() => {
    const world = worldRef.current;
    if (!world) return;
    const media = mediaRef.current;
    for (const peer of peersRef.current.values()) {
      world.upsertPeer?.(peer);
      // Re-apply remote streams if ontrack ran while world/entity was missing.
      const stream = media?.getRemoteStream(peer.id);
      if (stream) world.setPeerStream?.(peer.id, stream);
    }
    const local = media?.getLocalStream();
    if (local) {
      world.setLocalStream?.(local);
      setLocalStreamState(local);
    }
  }, [worldRef]);

  const disconnect = useCallback(() => {
    mediaRef.current?.stop();
    mediaRef.current = null;
    setLocalStreamState(null);
    setRemoteStreams({});
    sharingRef.current = false;
    setSharing(false);
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
    peersRef.current.clear();
    setPeerCount(0);
    setConnection("idle");
  }, []);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return;
    }

    let cancelled = false;
    setConnection("connecting");
    setError(null);

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/office`);
    wsRef.current = ws;

    const media = new SpatialMediaSession({
      sendSignal: (to, data) => send({ type: "signal", to, data }),
      onRemoteStream: (peerId, stream) => {
        worldRef.current?.setPeerStream?.(peerId, stream);
        setRemoteStreams((prev) => ({ ...prev, [peerId]: stream }));
      },
      onRemoteStreamGone: (peerId) => {
        worldRef.current?.setPeerStream?.(peerId, null);
        setRemoteStreams((prev) => {
          if (!(peerId in prev)) return prev;
          const next = { ...prev };
          delete next[peerId];
          return next;
        });
      },
      onLocalStreamChange: (stream) => {
        worldRef.current?.setLocalStream?.(stream);
        setLocalStreamState(stream);
      },
      onSharingChange: (next) => {
        sharingRef.current = next;
        setSharing(next);
        const pose = worldRef.current?.getPose?.();
        if (pose) {
          send({
            type: "state",
            ...pose,
            sharing: next,
            mapId: mapIdRef.current || "campus",
          });
        }
      },
      // Silent ICE recovery only re-ensures peers still in the room roster.
      isPeerPresent: (peerId) => peersRef.current.has(peerId),
    });
    mediaRef.current = media;
    setLocalStreamState(null);
    setRemoteStreams({});
    sharingRef.current = false;
    setSharing(false);

    ws.onopen = () => {
      if (cancelled) return;
      // Spawn is server-assigned; client coords are ignored by the room.
      send({
        type: "hello",
        name: nameRef.current,
        colors: colorsRef.current,
        status: statusRef.current,
        mic: micRef.current,
        cam: camRef.current,
        sharing: sharingRef.current,
        mapId: mapIdRef.current || "campus",
      });
    };

    ws.onerror = () => {
      if (cancelled) return;
      setError("Connection error");
      setConnection("error");
    };

    ws.onclose = (ev) => {
      if (cancelled) return;
      // 4000 = replaced by another tab/session for the same user.
      if (ev.code === 4000) {
        setError("Session replaced by another tab signed in as the same user");
        onToastRef.current(
          "Disconnected — another tab took this account. Use a second browser (or incognito) for admin + user.",
        );
      }
      setConnection("idle");
      media.stop();
    };

    ws.onmessage = (ev) => {
      if (cancelled) return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }

      const world = worldRef.current;

      switch (msg.type) {
        case "welcome": {
          selfIdRef.current = msg.selfId;
          setConnection("connected");
          const welcomeMap = msg.mapId || mapIdRef.current || "campus";
          mapIdRef.current = welcomeMap;
          setMapId(welcomeMap);
          // Apply published layout first so spawn/configure lands on valid geometry.
          void fetchAndApplyMapLayout(world, welcomeMap).then(() => {
            if (cancelled) return;
            worldRef.current?.configure?.({
              playerId: msg.selfId,
              name: nameRef.current,
              colors: colorsRef.current,
              gx: msg.gx,
              gy: msg.gy,
            });
          });
          // Place self + remotes immediately — do not wait on getUserMedia.
          world?.configure?.({
            playerId: msg.selfId,
            name: nameRef.current,
            colors: colorsRef.current,
            gx: msg.gx,
            gy: msg.gy,
          });
          if (Array.isArray(msg.offices)) {
            applyOffices(msg.offices);
          }
          peersRef.current.clear();
          for (const peer of msg.peers) {
            peersRef.current.set(peer.id, peer);
            world?.upsertPeer?.(peer);
          }
          setPeerCount(peersRef.current.size);
          flushPeersToWorld();
          // Spatial tip so testers don't file false media bugs for distance / rooms / focus.
          const place = welcomeMap === "plaza" ? "Plaza" : "HQ";
          const who =
            msg.peers.length > 0
              ? `Joined ${place} · ${msg.peers.length} online`
              : `Joined ${place}`;
          onToastRef.current(`${who} — ${SPATIAL_AV_TIP}`);

          // Media after presence so a permission prompt never blocks avatars.
          // Fetch ICE config once (STUN default; optional future TURN via env).
          void (async () => {
            const iceServers = await fetchOfficeIceServers();
            if (cancelled) return;
            media.setIceServers(iceServers);
            await media.start(msg.selfId, (id) => worldRef.current?.volumeOfId?.(id) ?? 0);
            if (cancelled) return;
            // Unlock spatial graph as soon as media is ready (gesture often
            // already happened via click-to-enter; still try here + gesture below).
            await media.resumeAudio();
            if (cancelled) return;

            const wantedMic = micRef.current;
            const wantedCam = camRef.current;
            media.setMic(wantedMic);
            media.setCam(wantedCam);

            // Re-acquire if user has mic/cam on but start yielded empty tracks.
            if (wantedMic || wantedCam) {
              await media.ensureLocalMedia({
                audio: wantedMic,
                video: wantedCam,
              });
              if (cancelled) return;
            }

            const local = media.getLocalStream();
            const hasAudio = (local?.getAudioTracks().length ?? 0) > 0;
            const hasVideo = (local?.getVideoTracks().length ?? 0) > 0;
            if (local) {
              worldRef.current?.setLocalStream?.(local);
              setLocalStreamState(local);
            }
            media.setMic(wantedMic);
            media.setCam(wantedCam);
            worldRef.current?.setMic?.(wantedMic);
            worldRef.current?.setCam?.(wantedCam);

            // Do NOT permanently disable mic/cam on the first empty gUM.
            // Ready-room auto-enter (?ready=1) often starts media without a fresh
            // user gesture on this document — browsers block getUserMedia until
            // the next click. Keep flags on; gesture handler re-acquires.
            if ((wantedMic && !hasAudio) || (wantedCam && !hasVideo)) {
              onToastRef.current(
                "Click anywhere in HQ to enable camera & mic (browser needs a gesture)",
              );
            }
            for (const peer of peersRef.current.values()) {
              media.ensurePeer(peer.id);
            }
          })();
          break;
        }
        case "peer-joined": {
          peersRef.current.set(msg.peer.id, msg.peer);
          world?.upsertPeer?.(msg.peer);
          media.ensurePeer(msg.peer.id);
          setPeerCount(peersRef.current.size);
          onToastRef.current(`${msg.peer.name} entered the office`);
          break;
        }
        case "peer-left": {
          peersRef.current.delete(msg.id);
          world?.removePeer?.(msg.id);
          media.removePeer(msg.id);
          setPeerCount(peersRef.current.size);
          break;
        }
        case "peer-state": {
          peersRef.current.set(msg.peer.id, msg.peer);
          world?.upsertPeer?.(msg.peer);
          break;
        }
        case "chat": {
          const vol = world?.volumeOfId?.(msg.from) ?? 0;
          const isSelf = msg.from === selfIdRef.current;
          if (!isSelf && vol <= 0.03) break;
          const zoneName = peersRef.current.get(msg.from)?.zoneId ?? "nearby";
          onChatRef.current({
            id: msg.id,
            from: msg.from,
            name: msg.name,
            color: msg.color,
            text: msg.text,
            zone: zoneName,
            vol: isSelf ? 1 : vol,
          });
          break;
        }
        case "action": {
          if (msg.from !== selfIdRef.current) {
            world?.peerAction?.(msg.from, msg.action);
            onPeerActionRef.current(msg.name, msg.action);
          }
          break;
        }
        case "wave": {
          // Legacy server/client path
          if (msg.from !== selfIdRef.current) {
            world?.peerAction?.(msg.from, "wave");
            onPeerActionRef.current(msg.name, "wave");
          }
          break;
        }
        case "signal": {
          void media.handleSignal(msg.from, msg.data);
          break;
        }
        case "offices-updated": {
          if (Array.isArray(msg.offices)) {
            applyOffices(msg.offices);
          }
          break;
        }
        case "map-layout-updated": {
          const updatedMap = msg.mapId || "campus";
          // Only refetch geometry for the map we're currently on.
          if (updatedMap !== (mapIdRef.current || "campus")) break;
          void fetchAndApplyMapLayout(worldRef.current, updatedMap).then(() => {
            onToastRef.current(
              updatedMap === "plaza"
                ? "Plaza map was updated by an admin"
                : "HQ map was updated by an admin",
            );
          });
          break;
        }
        case "error": {
          setError(msg.message);
          break;
        }
      }
    };

    const onPose = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        gx: number;
        gy: number;
        fx: number;
        fy: number;
        facing: string;
        status: string;
        mic: boolean;
        cam: boolean;
        zoneId: string;
      };
      send({
        type: "state",
        ...detail,
        sharing: sharingRef.current,
        mapId: mapIdRef.current || "campus",
      });
    };

    const onMapTransition = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        toMapId: string;
        toSpawn: { gx: number; gy: number };
        label?: string | null;
      };
      if (!detail?.toMapId || !detail.toSpawn) return;
      if (transitioningRef.current) return;
      if (detail.toMapId === (mapIdRef.current || "campus")) return;
      transitioningRef.current = true;

      const nextMap = detail.toMapId;
      const spawn = detail.toSpawn;
      const label = detail.label || nextMap;

      void (async () => {
        try {
          // Drop remote avatars from the previous map immediately (server will also
          // send peer-left / peer-joined; this avoids a one-frame ghost).
          const world = worldRef.current;
          for (const id of [...peersRef.current.keys()]) {
            peersRef.current.delete(id);
            world?.removePeer?.(id);
            mediaRef.current?.removePeer(id);
          }
          setPeerCount(0);

          mapIdRef.current = nextMap;
          setMapId(nextMap);
          await fetchAndApplyMapLayout(worldRef.current, nextMap);
          worldRef.current?.teleportPlayer?.(spawn.gx, spawn.gy);
          // Announce new map to the room so peers filter correctly.
          // Screen share is meeting-room only — stop when changing maps.
          void mediaRef.current?.stopScreenShare();
          const pose = worldRef.current?.getPose?.();
          if (pose) {
            send({
              type: "state",
              ...pose,
              gx: spawn.gx,
              gy: spawn.gy,
              fx: spawn.gx,
              fy: spawn.gy,
              sharing: sharingRef.current,
              mapId: nextMap,
            });
          } else {
            send({
              type: "state",
              gx: spawn.gx,
              gy: spawn.gy,
              fx: spawn.gx,
              fy: spawn.gy,
              facing: "SE",
              status: statusRef.current,
              mic: micRef.current,
              cam: camRef.current,
              sharing: sharingRef.current,
              zoneId: nextMap === "campus" || nextMap === "plaza" ? "campus_lawn" : "commons",
              mapId: nextMap,
            });
          }
          const destName = label || nextMap;
          onToastRef.current(`Entered ${destName}`);
        } finally {
          // Brief cooldown so standing on the destination spawn (if it were a
          // transition) or re-firing the same tile doesn't loop.
          window.setTimeout(() => {
            transitioningRef.current = false;
          }, 900);
        }
      })();
    };

    let poseAttached: VyllageWorldElement | null = null;
    const poseTimer = window.setInterval(() => {
      const el = worldRef.current;
      if (!el || poseAttached === el) return;
      if (poseAttached) {
        poseAttached.removeEventListener("vy-pose", onPose);
        poseAttached.removeEventListener("vyllage-map-transition", onMapTransition);
      }
      el.addEventListener("vy-pose", onPose);
      el.addEventListener("vyllage-map-transition", onMapTransition);
      poseAttached = el;
      // World may appear after welcome — re-paint remotes.
      flushPeersToWorld();
    }, 100);

    return () => {
      cancelled = true;
      window.clearInterval(poseTimer);
      if (poseAttached) {
        poseAttached.removeEventListener("vy-pose", onPose);
        poseAttached.removeEventListener("vyllage-map-transition", onMapTransition);
      }
      disconnect();
    };
  }, [enabled, disconnect, send, worldRef, flushPeersToWorld, applyOffices]);

  // Push mic/cam changes while connected; re-acquire tracks if enabling without them.
  useEffect(() => {
    if (connection !== "connected") return;
    const media = mediaRef.current;
    if (!media) return;

    let cancelled = false;
    void (async () => {
      // Apply desired enabled flags first so newly acquired tracks pick them up.
      media.setMic(mic);
      media.setCam(cam);
      if (mic || cam) {
        await media.ensureLocalMedia({ audio: mic, video: cam });
        if (cancelled) return;
        const local = media.getLocalStream();
        if (local) {
          worldRef.current?.setLocalStream?.(local);
          setLocalStreamState(local);
        }
      }
      if (cancelled) return;
      worldRef.current?.setMic?.(mic);
      worldRef.current?.setCam?.(cam);
    })();

    return () => {
      cancelled = true;
    };
  }, [mic, cam, connection, worldRef]);

  // AudioContext unlock + deferred getUserMedia on real user gesture.
  // Critical after Ready-room auto-enter (?ready=1): first gUM often runs without
  // activation and yields empty tracks; a click here re-acquires and attaches video.
  useEffect(() => {
    if (connection !== "connected") return;

    let cancelled = false;
    let hardAlignDone = false;
    let retryInFlight = false;

    const attachLocal = () => {
      const local = mediaRef.current?.getLocalStream() ?? null;
      if (local) {
        worldRef.current?.setLocalStream?.(local);
        setLocalStreamState(local);
      }
    };

    const hardAlignIfStillMissing = (wantedMic: boolean, wantedCam: boolean) => {
      if (hardAlignDone) return;
      const local = mediaRef.current?.getLocalStream() ?? null;
      const hasAudio = (local?.getAudioTracks().length ?? 0) > 0;
      const hasVideo = (local?.getVideoTracks().length ?? 0) > 0;
      let nextMic = wantedMic;
      let nextCam = wantedCam;
      if (wantedMic && !hasAudio) nextMic = false;
      if (wantedCam && !hasVideo) nextCam = false;
      if (nextMic === wantedMic && nextCam === wantedCam) return;

      hardAlignDone = true;
      mediaRef.current?.setMic(nextMic);
      mediaRef.current?.setCam(nextCam);
      worldRef.current?.setMic?.(nextMic);
      worldRef.current?.setCam?.(nextCam);
      onMediaFlagsAlignRef.current?.({ mic: nextMic, cam: nextCam });
      if (wantedCam && !hasVideo && wantedMic && !hasAudio) {
        onToastRef.current("Camera & mic unavailable — turned off");
      } else if (wantedCam && !hasVideo) {
        onToastRef.current("Camera unavailable — camera turned off");
      } else if (wantedMic && !hasAudio) {
        onToastRef.current("Microphone unavailable — mic turned off");
      }
      const pose = worldRef.current?.getPose?.();
      if (pose) {
        send({
          type: "state",
          ...pose,
          sharing: sharingRef.current,
          mapId: mapIdRef.current || "campus",
        });
      }
    };

    const onUserGesture = () => {
      if (cancelled || retryInFlight) return;
      const media = mediaRef.current;
      if (!media) return;

      retryInFlight = true;
      void (async () => {
        try {
          await media.resumeAudio();
          if (cancelled) return;

          const wantedMic = micRef.current;
          const wantedCam = camRef.current;
          if (!wantedMic && !wantedCam) return;

          const before = media.getLocalStream();
          const needAudio = wantedMic && (before?.getAudioTracks().length ?? 0) === 0;
          const needVideo = wantedCam && (before?.getVideoTracks().length ?? 0) === 0;
          if (!needAudio && !needVideo) {
            attachLocal();
            return;
          }

          media.setMic(wantedMic);
          media.setCam(wantedCam);
          await media.ensureLocalMedia({ audio: wantedMic, video: wantedCam });
          if (cancelled) return;
          attachLocal();
          // Only after a real gesture failed to produce tracks do we force flags off.
          hardAlignIfStillMissing(wantedMic, wantedCam);
        } finally {
          retryInFlight = false;
        }
      })();
    };

    // Immediate audio unlock attempt (may no-op without gesture).
    void mediaRef.current?.resumeAudio();
    window.addEventListener("pointerdown", onUserGesture);
    window.addEventListener("keydown", onUserGesture);
    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onUserGesture);
      window.removeEventListener("keydown", onUserGesture);
    };
  }, [connection, send, worldRef]);

  useEffect(() => {
    if (connection !== "connected") return;
    worldRef.current?.configure?.({ name, colors });
  }, [name, colors, connection, worldRef]);

  const sendChat = useCallback(
    (text: string) => {
      send({ type: "chat", text });
    },
    [send],
  );

  const sendAction = useCallback(
    (action: OfficeActionId) => {
      send({ type: "action", action });
    },
    [send],
  );

  const startScreenShare = useCallback(async (): Promise<boolean> => {
    const media = mediaRef.current;
    if (!media) {
      onToastRef.current("Join HQ multiplayer before sharing your screen");
      return false;
    }
    if (connection !== "connected") {
      onToastRef.current("Still connecting — try Share again in a moment");
      return false;
    }
    const result = await media.startScreenShare();
    if (result === "started" || result === "already") return true;
    if (result === "cancelled") {
      // User closed the picker — quiet is fine, but still surface something subtle.
      onToastRef.current("Screen share cancelled");
      return false;
    }
    if (result === "not-ready") {
      onToastRef.current("Media not ready yet — click the map once, then try Share again");
      return false;
    }
    // blocked — Permissions-Policy, insecure context, or browser deny
    onToastRef.current(
      "Screen share blocked — allow display capture for this site (or open outside an iframe)",
    );
    return false;
  }, [connection]);

  const stopScreenShare = useCallback(async (): Promise<void> => {
    await mediaRef.current?.stopScreenShare();
  }, []);

  return {
    connection,
    peerCount,
    error,
    localStream,
    remoteStreams,
    mapId,
    sharing,
    startScreenShare,
    stopScreenShare,
    sendChat,
    sendAction,
    disconnect,
  };
}
